<template>
  <SvgSprite />
  <div class="as-content-page as-match-detail" ref="asMatchDetail">
    <TabsIcon
      :tabs="tabs"
      :activeTab="activeTab"
      :isMin="isChatTyping"
      @change="changeTab"
    />

    <div
      id="as-match-detail-content"
      class="as-match-detail-content"
      :class="{
        'as-hide-content': isHideContent,
      }"
      :style="{
        height: `${heightContent}px`,
      }"
    >
      <div v-show="activeTab === 'chat'">
        <IframeChat />
      </div>

      <div v-if="activeTab === 'statistics'">
        <StatisticsList
          v-if="tabLoading.statistics"
          :match="match"
          :statistics="statistics"
        />
        <H2hListSkeleton v-else />
      </div>
      <div v-else-if="activeTab === 'overview'">
        <IncidentsList
          v-if="tabLoading.overview"
          :match="match"
          :incidents="incidents"
        />
        <H2hListSkeleton v-else />
      </div>
      <div v-else-if="activeTab === 'bracket'">
        <BracketTabList
          v-if="tabLoading.bracket"
          :match="match"
          :tournament="tournament"
        />
        <H2hListSkeleton v-else />
      </div>
      <div v-else-if="activeTab === 'h2h'" class="as-badminton">
        <h2hList
          v-if="tabLoading.h2h || tabLoading.next_match || isObjectNotEmpty(h2h)"
          :match="match"
          :h2h="h2h"
        />
        <H2hListSkeleton v-else />
      </div>
      <div v-else-if="activeTab === 'next_match'" class="as-badminton">
        <div
          :class="`as-parent-h2h as-parent-h2h__item-h-auto as-parent-h2h-${siteName}`"
        >
          <NextMatchList
            v-if="tabLoading.h2h || tabLoading.next_match || isObjectNotEmpty(h2h)"
            :match="match"
            :h2h="h2h"
          />
          <H2hListSkeleton v-else />
        </div>
      </div>
      <div
        v-else-if="activeTab === 'match_other'"
        :class="`as-parent-h2h as-parent-h2h__item-h-auto as-parent-h2h-${siteName}`"
      >
        <MatchOther
          v-if="tabLoading.match_other"
          :matchLiveData="matchOtherData"
          :matchId="matchId"
        />
        <H2hListSkeleton v-else />
      </div>
      <div v-else-if="activeTab === 'odd'">
        <OddTab
          :odd="odd"
          :oddOld="oddOld"
          :tabKey="tabKey.odd"
        />
      </div>
      <div v-else-if="activeTab === 'information'">
        <Information :match="match" />
      </div>
      <div
        v-else-if="activeTab === 'standing'"
        class="as-h-100 as-position-relative"
      >
        <StandingListTab
          v-if="tabLoading.standing"
          :match="match"
          :standing="standing"
        />
        <StandingListTabSkeleton v-else />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import H2hListSkeleton from "~src/components/skeleton/result-popup/H2hListSkeleton.vue";
import StandingListTabSkeleton from "~src/components/skeleton/standing/StandingListTabSkeleton.vue";
import {
  matchStatistics,
  matchH2h,
  matchDetail,
  matchDetailLive,
  getRealtimeDetail,
  seasonBracketMatch,
  matchStanding,
} from "~src/services/SpapiService";
import { getOddsMatch } from "~src/services/OddService";
import { getMatchLive } from "~src/services/RemoteLiveService";
import { mapMatchData, initializeData } from "~src/helpers/volleyball";
import { addMatchDetailLive, mapTeamToH2H } from "~src/helpers/tennis";
import type {
  IMatch,
  IOddMatch,
  IItemString,
  IStatsJsonData,
  IIncidentRealTime,
} from "~src/interfaces/badminton";
import IncidentsList from "~src/components/incidents/volleyball/IncidentsList.vue";
import StatisticsList from "~src/components/statistics/volleyball/StatisticsList.vue";
import H2hList from "~src/components/h2h/tennis/H2hList.vue";
import Information from "~src/components/matches/volleyball/Information.vue";
import MatchOther from "~src/components/matches/tennis/MatchOther.vue";
import NextMatchList from "~src/components/matches/tennis/NextMatchList.vue";
import OddTab from "~src/components/odds/OddTab.vue";
import IframeChat from "~src/components/IframeChat.vue";
import IconStats from "~src/components/icons/menus/IconStats.vue";
import IconSummary from "~src/components/icons/menus/IconSummary.vue";
import IconChat from "~src/components/icons/menus/IconChat.vue";
import IconH2h from "~src/components/icons/menus/IconH2h.vue";
import IconMatchOther from "~src/components/icons/menus/IconMatchOther.vue";
import IconOdd from "~src/components/icons/menus/IconOdd.vue";
import IconInfo from "~src/components/icons/menus/IconInfo.vue";
import IconLive from "~src/components/icons/menus/IconLive.vue";
import IconSchematic from "~src/components/icons/menus/tennis/IconSchematic.vue";
import SvgSprite from "~src/components/matches/SvgSprite.vue";
import MatchEnum from "~src/enums/volleyball/MatchEnum";
import { useMatchDetailStore } from "~src/stores/MatchDetailStore";
import TabsIcon from "~src/components/TabsIcon.vue";
import BracketTabList from "~src/components/bracket/tennis/BracketTabList.vue";
import type { ITournamentJsonData } from "~src/interfaces/tennis";
import type { IH2h, IMatchLiveData } from "~src/interfaces/tennis";
import IconStanding from "~src/components/icons/menus/IconStanding.vue";
import StandingListTab from "~src/components/standings/volleyball/StandingListTab.vue";
import { isObjectNotEmpty } from "~src/helpers";
import { EVENT_ODDS_LIVE_DATA_UPDATED } from "~src/utils/constant";

export default defineComponent({
  name: "matchDetail",
  components: {
    H2hListSkeleton,
    StandingListTabSkeleton,
    IncidentsList,
    StatisticsList,
    H2hList,
    MatchOther,
    NextMatchList,
    OddTab,
    Information,
    IframeChat,
    SvgSprite,
    TabsIcon,
    BracketTabList,
    StandingListTab
  },
  props: {
    matchId: {
      type: String,
    },
    apiLiveUrl: {
      type: String,
    },
    isFetchOdd: {
      type: Boolean
    }
  },
  data() {
    const matchDetailStore = useMatchDetailStore();

    return {
      tournament: {} as ITournamentJsonData | {},
      matchDetailStore,
      match: {} as IMatch,
      incidents: {} as IIncidentRealTime,
      statistics: [] as IStatsJsonData[] | any,
      h2h: {} as IH2h,
      standing: null,
      lineups: null,
      shootingPointData: null as any,
      detailShooting: null as any,
      data: null,
      odd: [] as IOddMatch[],
      oddOld: [] as IOddMatch[],
      matchOtherData: {} as IMatchLiveData,
      matchOthers: [] as Array<IMatch>,
      isLoading: false,
      isLoadingInterval: false,
      isShowChat: true,
      tabs: [
        {
          key: "statistics",
          title: "Thông số",
          icon: IconStats,
        },
        {
          key: "overview",
          title: "Diễn biến",
          icon: IconSummary,
        },
        {
          key: "bracket",
          title: "Sơ đồ",
          icon: IconSchematic,
        },
        {
          key: "standing",
          title: "BXH",
          icon: IconStanding,
        },
        {
          key: "chat",
          title: "Chat",
          icon: IconChat,
        },
        {
          key: "h2h",
          title: "Đối đầu",
          icon: IconH2h,
        },
        {
          key: "match_other",
          title: "Trận khác",
          icon: IconLive,
        },
        {
          key: "next_match",
          title: "Trận sắp tới",
          icon: IconMatchOther,
        },
        {
          key: "odd",
          title: "Tỷ lệ kèo",
          icon: IconOdd,
        },
        {
          key: "information",
          title: "TT trận",
          icon: IconInfo,
        },
      ],
      tabLoading: {} as IItemString,
      tabKey: {} as IItemString,
      timers: [] as NodeJS.Timeout[]
    };
  },
  computed: {
    activeTab(): string {
      return this.matchDetailStore.getActiveTab;
    },
    isChatTyping(): boolean {
      return this.matchDetailStore.getIsChatTyping;
    },
    isHideContent(): boolean {
      return this.matchDetailStore.getIsHideContent;
    },
    heightContent(): number {
      return this.matchDetailStore.getHeightContent;
    },
    sportType() {
      // @ts-ignore
      return this.gSportType;
    },
    siteName() {
      // @ts-ignore
      return this.gSiteName;
    },
  },
  async created() {
    try {
      const rsMatch = await matchDetail(this.matchId, this.sportType);
      this.match =
        rsMatch.data.data && rsMatch.data.data.data
        ? rsMatch.data.data.data
        : rsMatch.data.data;

      await this.changeTab("chat");
    } catch (e) {
      console.log("Load match detail error", e);
    }
  },
  async mounted() {
    const element = this.$refs.asMatchDetail as HTMLElement;
    if (element) {
      // @ts-ignore
      this.matchDetailStore.setScreenWidth(
        element?.getBoundingClientRect().width
      );
    }

    const interval1 = setInterval(async () => {
      if (
        this.match.status_id !== MatchEnum.ENDED &&
        !this.isLoadingInterval &&
        this.activeTab !== "odd"
      ) {
        this.isLoadingInterval = true;
        await this.changeTab(this.activeTab, true);
        this.isLoadingInterval = false;
      }
    }, 21000);
    this.timers.push(interval1)

    if (this.isFetchOdd) {
      const interval2 = setInterval(async () => {
        if (
          this.match.status_id !== MatchEnum.ENDED &&
          !this.isLoadingInterval &&
          this.activeTab === "odd"
        ) {
          this.isLoadingInterval = true;
          await this.changeTab(this.activeTab, true);
          this.isLoadingInterval = false;
        }
      }, 5000);
      this.timers.push(interval2)
    } else {
      window.addEventListener(EVENT_ODDS_LIVE_DATA_UPDATED, this.handleOddsLiveDataUpdated);
    }
  },
  async beforeUnmount() {
    this.timers.forEach(clearInterval);

    if (!this.isFetchOdd) {
      window.removeEventListener(EVENT_ODDS_LIVE_DATA_UPDATED, this.handleOddsLiveDataUpdated);
    }
  },
  methods: {
    async changeTab(tabName: string, isInterval = false) {
      this.matchDetailStore.setActiveTab(tabName);
      const matchId = this.matchId;
      const seasonId = this.match?.season_id ?? "";
      this.isLoading = true;

      if (tabName === "statistics") {
        try {
          const { data: { data: statistics = [] }} = await matchStatistics(matchId, this.sportType);
          const _statistics = mapMatchData(statistics);
          this.statistics = _statistics.length ? _statistics : [initializeData()];
        } catch (e) {
          console.log("Load statistics error", e);
        }
      } else if (tabName === "overview") {
        try {
          const { data: { data: incidents = { match: {} as IMatch, data: [] } }} = await getRealtimeDetail(matchId, this.sportType);
          this.incidents = incidents;
        } catch (e) {
          console.log("Load incidents error", e);
        }
      } else if (tabName === "h2h" || tabName === "next_match") {
        try {
          const { data: { data: h2h = [] }} = await matchH2h(matchId, this.sportType);
          const __h2h = mapTeamToH2H(h2h, this.sportType);
          this.h2h = __h2h;
        } catch (e) {
          console.log("Load h2h error", e);
        }
      } else if (tabName === "match_other") {
        try {
          let [rsMatch, rsLive] = await Promise.all([
            getMatchLive(this.apiLiveUrl, this.sportType, this.siteName),
            matchDetailLive(this.sportType),
          ]);

          const teams = rsMatch.data.teams ?? [];
          const matches = await addMatchDetailLive(
            rsMatch.data.matches,
            rsLive.data.results,
            teams,
            this.sportType
          );

          this.matchOtherData = {
            competitions: rsMatch.data.competitions,
            teams: teams,
            commentators: rsMatch.data.commentators,
            matches,
          } as IMatchLiveData;
        } catch (e) {
          console.log("Load match other error", e);
        }
      } else if (tabName === "bracket") {
        try {
          const { data: { data: tournament = {} }} = await seasonBracketMatch(seasonId, this.sportType);
          this.tournament = tournament;
        } catch (e) {
          console.log("Load bracket error", e);
        }
      } else if (tabName === "odd") {
        try {
          const { data: { data: odd = [] }} = await getOddsMatch(this.sportType, this.matchId);
          this.oddOld = JSON.parse(JSON.stringify(this.odd));
          this.odd = odd && odd.length ? odd : [];
        } catch (e) {
          console.log("Load odd error", e);
        }
      } else if (tabName === "standing") {
        if (seasonId) {
          try {
            const { data: { data: standing = {} }} = await matchStanding(seasonId, this.sportType);
            this.standing = {
              match: this.match,
              ...standing,
            };
          } catch (e) {
            console.log("Load standing error", e);
          }
        }
      }
      this.isLoading = false;
      this.tabKey[tabName] = Date.now();
      this.tabLoading[tabName] = true;
    },
    isObjectNotEmpty,
    handleOddsLiveDataUpdated(e: Event) {
      const oddsData = (e as CustomEvent<IOddMatch[]>).detail;
      if (oddsData && this.activeTab === "odd") {
        this.oddOld = JSON.parse(JSON.stringify(this.odd));
        this.odd = oddsData;
      }
    },
  },
});
</script>

<style scoped></style>
